import React, { useEffect, useState, useCallback } from 'react';
import { systemAuditLogsApi, usersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Search, 
  Download, 
  RefreshCw, 
  Shield, 
  User, 
  Monitor, 
  Globe,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  LogIn,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Upload,
  CheckCircle,
  XCircle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

// Action icon mapping
const getActionIcon = (action) => {
  switch (action) {
    case 'login':
      return <LogIn className="w-4 h-4 text-green-600" />;
    case 'logout':
      return <LogOut className="w-4 h-4 text-gray-600" />;
    case 'create':
      return <Plus className="w-4 h-4 text-blue-600" />;
    case 'update':
      return <Pencil className="w-4 h-4 text-amber-600" />;
    case 'delete':
      return <Trash2 className="w-4 h-4 text-red-600" />;
    case 'upload':
      return <Upload className="w-4 h-4 text-purple-600" />;
    case 'approve':
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    case 'reject':
      return <XCircle className="w-4 h-4 text-red-600" />;
    case 'submit':
      return <FileText className="w-4 h-4 text-blue-600" />;
    default:
      return <Shield className="w-4 h-4 text-gray-500" />;
  }
};

// Action badge colors
const getActionBadgeClass = (action) => {
  switch (action) {
    case 'login':
      return 'bg-green-100 text-green-800';
    case 'logout':
      return 'bg-gray-100 text-gray-800';
    case 'create':
      return 'bg-blue-100 text-blue-800';
    case 'update':
      return 'bg-amber-100 text-amber-800';
    case 'delete':
      return 'bg-red-100 text-red-800';
    case 'upload':
      return 'bg-purple-100 text-purple-800';
    case 'approve':
      return 'bg-green-100 text-green-800';
    case 'reject':
      return 'bg-red-100 text-red-800';
    case 'submit':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Format date
const formatDateTime = (dateStr) => {
  try {
    const date = parseISO(dateStr);
    return format(date, 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
};

export const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Filter options
  const [actions, setActions] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 25;

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [actionsData, modulesData, usersData] = await Promise.all([
        systemAuditLogsApi.getActions(),
        systemAuditLogsApi.getModules(),
        systemAuditLogsApi.getUsers()
      ]);
      setActions(actionsData || []);
      setModules(modulesData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        ...(search && { search }),
        ...(userFilter && { user_filter: userFilter }),
        ...(actionFilter && { action_filter: actionFilter }),
        ...(moduleFilter && { module_filter: moduleFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };
      
      const [logsData, countData] = await Promise.all([
        systemAuditLogsApi.getAll(params),
        systemAuditLogsApi.getCount(params)
      ]);
      
      setLogs(logsData || []);
      setTotalCount(countData?.count || 0);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, userFilter, actionFilter, moduleFilter, startDate, endDate]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, userFilter, actionFilter, moduleFilter, startDate, endDate]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        ...(search && { search }),
        ...(userFilter && { user_filter: userFilter }),
        ...(actionFilter && { action_filter: actionFilter }),
        ...(moduleFilter && { module_filter: moduleFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };
      
      const blob = await systemAuditLogsApi.export(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Audit logs exported successfully');
    } catch (error) {
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setUserFilter('');
    setActionFilter('');
    setModuleFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = search || userFilter || actionFilter || moduleFilter || startDate || endDate;

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && logs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Shield className="w-6 h-6 text-[#6a5da4]" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">Audit Logs</h1>
            <p className="text-muted-foreground text-sm mt-1 hidden sm:block">
              Track all system activities for security and monitoring
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? 'bg-purple-50 border-purple-200' : ''}
          >
            <Filter className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <span className="ml-1 sm:ml-2 px-1.5 py-0.5 text-xs bg-purple-600 text-white rounded-full">
                !
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLogs}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || logs.length === 0}
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export CSV'}</span>
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="space-y-2">
                <Label className="text-sm">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Name, email, description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-white"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* User Filter */}
              <div className="space-y-2">
                <Label className="text-sm">User</Label>
                <Select value={userFilter} onValueChange={(val) => setUserFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="bg-white" data-testid="user-filter">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.userId} value={u.userId}>
                        {u.userName} ({u.userEmail})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Action</Label>
                <Select value={actionFilter} onValueChange={(val) => setActionFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="bg-white" data-testid="action-filter">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {actions.map((action) => (
                      <SelectItem key={action} value={action}>
                        {action.charAt(0).toUpperCase() + action.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Module Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Module</Label>
                <Select value={moduleFilter} onValueChange={(val) => setModuleFilter(val === 'all' ? '' : val)}>
                  <SelectTrigger className="bg-white" data-testid="module-filter">
                    <SelectValue placeholder="All modules" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All modules</SelectItem>
                    {modules.map((module) => (
                      <SelectItem key={module} value={module}>
                        {module.charAt(0).toUpperCase() + module.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <Label className="text-sm">From Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white"
                  data-testid="start-date"
                />
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <Label className="text-sm">To Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white"
                  data-testid="end-date"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className="text-purple-600 hover:bg-purple-100"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-blue-700">{totalCount}</div>
            <div className="text-sm text-blue-600">Total Logs</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-green-700">
              {logs.filter(l => l.action === 'login').length}
            </div>
            <div className="text-sm text-green-600">Logins (this page)</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-amber-700">
              {logs.filter(l => ['create', 'update', 'delete'].includes(l.action)).length}
            </div>
            <div className="text-sm text-amber-600">Changes (this page)</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-bold text-purple-700">
              {logs.filter(l => ['approve', 'reject'].includes(l.action)).length}
            </div>
            <div className="text-sm text-purple-600">Approvals (this page)</div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No audit logs found</h3>
              <p className="text-muted-foreground text-sm">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'System activities will appear here'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Date & Time</TableHead>
                      <TableHead className="font-semibold">User</TableHead>
                      <TableHead className="font-semibold">Action</TableHead>
                      <TableHead className="font-semibold">Module</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold">IP Address</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold">Device / Browser</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-gray-50" data-testid={`log-row-${log.id}`}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDateTime(log.createdAt)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{log.userName}</p>
                              <p className="text-xs text-muted-foreground">{log.userEmail}</p>
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">
                                {log.userRole}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadgeClass(log.action)}`}>
                              {log.action.toUpperCase()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {log.module}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="text-sm truncate" title={log.description}>
                            {log.description}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Globe className="w-3 h-3" />
                            {log.ipAddress || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.location ? (
                            <div className="flex items-center gap-1 text-sm text-green-700">
                              <MapPin className="w-3 h-3" />
                              {log.location}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Monitor className="w-3 h-3" />
                            <span>{log.device || 'Unknown'}</span>
                            {log.browser && log.browser !== 'Unknown' && (
                              <span className="text-xs">/ {log.browser}</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="bg-white border rounded-lg p-4 shadow-sm" data-testid={`log-card-${log.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{log.userName}</p>
                          <p className="text-xs text-muted-foreground">{log.userEmail}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {getActionIcon(log.action)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionBadgeClass(log.action)}`}>
                          {log.action.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm mb-3">{log.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        <p className="font-medium text-gray-600">Date</p>
                        <p>{formatDateTime(log.createdAt)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Module</p>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                          {log.module}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">IP Address</p>
                        <p>{log.ipAddress || '-'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Location</p>
                        <p className="text-green-700">{log.location || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="font-medium text-gray-600">Device</p>
                        <p>{log.device || 'Unknown'} / {log.browser || 'Unknown'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination - Always show */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-4">
                <p className="text-sm text-muted-foreground text-center sm:text-left">
                  {totalCount > 0 ? (
                    <>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} logs</>
                  ) : (
                    <>No logs to display</>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="ml-1">Previous</span>
                  </Button>
                  <div className="flex items-center gap-1">
                    {totalPages > 0 && Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          className={`w-8 h-8 p-0 ${currentPage === pageNum ? 'bg-[#6a5da4] hover:bg-[#5a4d94]' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    data-testid="pagination-next"
                  >
                    <span className="mr-1">Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
